import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { heroSubtitleClass, heroTitleClass, motionSafe } from '../constants/heroTypography';
import heroBg from '../assets/img/ic_our_service_background.png';
import pdfCompanyProfile from '../assets/pdf/RAYANSTAR-Company-Profile.pdf';
import tourismTravelPkg from '../assets/img/tourism_travel_pkg.jpg';
import educationPackage from '../assets/img/education_package.jpg';
import realStateImg from '../assets/img/real_state.jpg';

const serviceCategories = [
  {
    title: 'Visa & Immigration Services',
    items: ['Work Visa', 'Tourist Visa', 'Job Visa & Work Permit Processing'],
  },
  {
    title: 'Event & Travel Services',
    items: [
      'Event Management',
      'Tour Packages',
      'Travel Insurance',
      'Staycation Services',
      'Limousine Services',
    ],
  },
  {
    title: 'Education & Student Services',
    items: [
      'Student Visas',
      'Study Abroad Guidance',
      'Admission Support',
      '90+ My Tuition App',
      'STEP IAS (Online coaching)',
    ],
  },
  {
    title: 'Business & Professional Services',
    items: ['Real Estate Services', 'NHRA Services', 'Profiles & Documentation Support'],
  },
];

const visaTravelDetails = [
  {
    title: 'GCC Visa Services',
    body:
      'Assistance with visa applications for GCC countries, including documentation and quick processing.',
  },
  {
    title: 'Global Visa Services',
    body: 'Support for tourist, business, and visit visas for multiple international destinations.',
  },
];

const tourDetails = [
  { title: 'Tour Packages', body: 'Custom-made local and international holiday packages.' },
  { title: 'Tours & Travel Arrangements', body: 'Complete travel bookings—tickets, hotels, transport.' },
  { title: 'Travel Insurance', body: 'Affordable and reliable travel insurance plans.' },
  { title: 'Staycation Services', body: 'Local hotel stays and relaxation packages.' },
];

const educationDetails = [
  { title: 'Student Visas', body: 'Easy and guided student visa processing.' },
  { title: 'Study Abroad Guidance', body: 'Advice on choosing the right country, course, and university.' },
  {
    title: 'Admission Support',
    body: 'Help with applications, documentation, and admission steps.',
  },
];

function SectionHeading({ title }) {
  return (
    <div className="text-center">
      <div className="mx-auto h-px w-16 bg-gradient-to-r from-transparent via-gold/80 to-transparent" />
      {title ? (
        <h2 className="mt-6 font-sans text-2xl font-bold tracking-tight text-white sm:text-3xl">{title}</h2>
      ) : null}
    </div>
  );
}

export default function Profile() {
  return (
    <div className="bg-navy-950">
      <PageHero
        title={
          <>
            <span className="block">Bahrain&apos;s Leading Consultancy Services</span>
          </>
        }
        subtitle="Company Profile"
        backgroundImage={heroBg}
        titleClassName={heroTitleClass}
        subtitleClassName={heroSubtitleClass(motionSafe)}
      >
        <div
          className={`mx-auto mt-8 flex max-w-2xl flex-col items-center justify-center gap-3 sm:mt-10 sm:flex-row sm:flex-wrap sm:gap-4 ${motionSafe} animate-fade-in-up [animation-delay:360ms]`}
          role="group"
          aria-label="PDF downloads"
        >
          <a
            href={pdfCompanyProfile}
            download="RAYANSTAR-Company-Profile.pdf"
            className="inline-flex w-full min-w-[220px] items-center justify-center gap-2 rounded-md border border-gold/90 bg-white/5 px-5 py-3 font-sans text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-sm transition hover:bg-gold/15 sm:w-auto sm:text-sm sm:tracking-[0.2em]"
          >
            <svg className="h-4 w-4 shrink-0 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
            Download company profile
          </a>
         
        </div>
      </PageHero>

      <div className="mx-auto max-w-5xl px-4 pb-20 pt-12 text-left sm:px-6 lg:px-8 lg:pb-28">
        <section className="mx-auto max-w-3xl">
          <p className="text-justify text-base leading-relaxed text-white/80">
            Welcome to RAYAN STAR Consultancy and Services. Our team of certified and experienced
            consultants is committed to upholding the highest standards of integrity and transparency. We
            stay updated with the latest immigration policies and procedures to provide accurate and
            effective guidance.
          </p>
        </section>

        <section className="mt-16">
          <SectionHeading title="Our Services" />
          <p className="mx-auto mt-6 max-w-2xl text-justify text-sm leading-relaxed text-white/70">
            Overview of our main service areas — hover cards for a quick view of what we offer.
          </p>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
            {serviceCategories.map((cat, idx) => (
              <article
                key={cat.title}
                className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-md transition hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-xl motion-reduce:hover:translate-y-0 ${motionSafe} animate-fade-in-up`}
                style={{ animationDelay: `${120 + idx * 90}ms` }}
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background: 'radial-gradient(520px 260px at 50% 10%, rgba(212,175,55,0.16), transparent 60%)',
                  }}
                  aria-hidden
                />
                <h3 className="relative font-serif text-lg font-semibold text-navy">{cat.title}</h3>
                <ul className="relative mt-4 space-y-2 text-left text-sm text-navy/75">
                  {cat.items.map((item, i) => (
                    <li key={item} className="flex gap-2">
                      <span className="mt-0.5 shrink-0 font-medium text-gold">{i + 1}.</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="mt-14 text-center">
            <div className="mx-auto h-px w-16 bg-gradient-to-r from-transparent via-gold/80 to-transparent" />
            <h3 className="mt-4 font-sans text-lg font-semibold text-white">Core Services</h3>
          </div>
          <article
            className={`group relative mt-10 overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 text-left shadow-md transition hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-xl motion-reduce:hover:translate-y-0 ${motionSafe} animate-fade-in-up`}
            style={{ animationDelay: '200ms' }}
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              style={{
                background: 'radial-gradient(520px 260px at 50% 10%, rgba(212,175,55,0.16), transparent 60%)',
              }}
              aria-hidden
            />
            <h4 className="relative text-center font-serif text-lg font-semibold text-navy">Medical Tourism to India</h4>
            <p className="relative mt-4 text-justify text-sm leading-relaxed text-navy/75">
              Experience world-class healthcare with complete peace of mind. We connect you with top
              hospitals and specialists while managing every aspect of your journey—from travel and
              accommodation to personalized care and recovery support.
            </p>
            <div className="relative mt-4 text-center">
              <Link
                to="/services/medical-tourism"
                className="text-sm font-semibold text-gold hover:underline"
              >
                View full service details →
              </Link>
            </div>
          </article>
        </section>

        <section className="mt-20">
          <SectionHeading title="Visa & Travel Services" />
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {visaTravelDetails.map((block) => (
              <div
                key={block.title}
                className="rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-md"
              >
                <h3 className="text-center font-serif text-lg font-semibold text-gold">{block.title}</h3>
                <p className="mt-3 text-justify text-sm leading-relaxed text-navy/75">{block.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <div className="mx-auto h-px w-16 bg-gradient-to-r from-transparent via-gold/80 to-transparent" />
            <h3 className="mt-4 font-sans text-lg font-semibold text-white">Tour Packages</h3>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {tourDetails.map((row) => (
              <div key={row.title} className="rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm">
                <p className="text-center font-serif font-semibold text-navy">{row.title}</p>
                <p className="mt-2 text-justify text-sm text-navy/70">{row.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 overflow-hidden rounded-2xl border border-slate-200 shadow-md ring-1 ring-gold/10">
            <img
              src={tourismTravelPkg}
              alt="Tourism and travel packages"
              className="mx-auto h-auto w-full max-h-[280px] object-cover"
            />
          </div>
        </section>

        <section className="mt-20">
          <SectionHeading title="Education & Student Services" />
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {educationDetails.map((row, idx) => (
              <article
                key={row.title}
                className={`group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-md transition hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-xl motion-reduce:hover:translate-y-0 ${motionSafe} animate-fade-in-up`}
                style={{ animationDelay: `${120 + idx * 100}ms` }}
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background: 'radial-gradient(520px 260px at 50% 10%, rgba(212,175,55,0.16), transparent 60%)',
                  }}
                  aria-hidden
                />
                <h3 className="relative text-center font-serif text-lg font-semibold text-navy">{row.title}</h3>
                <p className="relative mt-2 text-justify text-sm leading-relaxed text-navy/75">{row.body}</p>
              </article>
            ))}
          </div>
          <div className="mt-10 overflow-hidden rounded-2xl border border-slate-200 shadow-md ring-1 ring-gold/10">
            <img
              src={educationPackage}
              alt="Education and student services"
              className="mx-auto h-auto w-full max-h-[280px] object-cover"
            />
          </div>
        </section>

        <section className="mt-20">
          <SectionHeading title="Business Services" />
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
              <h2 className="text-center font-serif text-lg font-semibold text-navy">Real Estate Services</h2>
              <p className="mt-3 text-justify text-sm leading-relaxed text-navy/75">
                We provide assistance with property buying, selling, renting, and documentation support,
                ensuring smooth and reliable service for clients.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
              <h2 className="text-center font-serif text-lg font-semibold text-navy">NHRA Services</h2>
              <p className="mt-3 text-justify text-sm leading-relaxed text-navy/75">
                We handle NHRA applications, licensing, renewals, dataflow, and approval procedures quickly
                and accurately to make the process easy for you.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
              <h2 className="text-center font-serif text-lg font-semibold text-navy">Limousine Services</h2>
              <p className="mt-3 text-justify text-sm leading-relaxed text-navy/75">
                Premium chauffeur-driven limousine service for airport transfers, events, corporate travel,
                and special occasions—comfortable, stylish, and reliable.
              </p>
            </div>
          </div>

          <div className="mt-12 overflow-hidden rounded-2xl border border-slate-200 shadow-md ring-1 ring-gold/10">
            <img
              src={realStateImg}
              alt="Real estate and professional services"
              className="mx-auto h-auto w-full max-h-[280px] object-cover"
            />
          </div>
        </section>
      </div>
    </div>
  );
}
